import React, { useState, useEffect, useRef } from 'react';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useCompany } from '@/context/CompanyContext';
import { BarcodeScanner } from '@/components/inventory/BarcodeScanner';
import { useBarcodeCache } from '@/hooks/useBarcodeCache';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Package, Loader2, Camera, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Product } from '@/context/SupabaseInventoryContext';

interface SupabaseAddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingProduct?: Product | null;
}

export const SupabaseAddProductModal: React.FC<SupabaseAddProductModalProps> = ({
  isOpen,
  onClose,
  editingProduct,
}) => {
  const { addProduct, updateProduct } = useSupabaseInventory();
  const { userRole } = useCompany();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    quantity: '',
    min_threshold: '',
    barcode: '',
    image_url: '',
  });
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lookingUpBarcode, setLookingUpBarcode] = useState(false);
  const barcodeSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);

  const isEdit = Boolean(editingProduct);
  const isAdmin = userRole === 'admin';

  useEffect(() => {
    if (isOpen) {
      if (editingProduct) {
        setFormData({
          name: editingProduct.name,
          price: editingProduct.price.toString(),
          quantity: editingProduct.quantity.toString(),
          min_threshold: editingProduct.min_threshold.toString(),
          barcode: editingProduct.barcode || '',
          image_url: editingProduct.image_url || '',
        });
        setImagePreview(editingProduct.image_url || null);
      } else {
        setFormData({
          name: '',
          price: '2',
          quantity: '10',
          min_threshold: '10',
          barcode: '',
          image_url: '',
        });
        setSelectedFile(null);
        setImagePreview(null);
      }
      
      // Focus sur le champ code-barres après un court délai
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, editingProduct]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdmin) {
      toast({
        title: "Accès refusé",
        description: "Seuls les administrateurs peuvent modifier les produits",
        variant: "destructive",
      });
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom du produit est requis",
        variant: "destructive",
      });
      return;
    }

    const price = parseFloat(formData.price);
    const quantity = parseInt(formData.quantity);
    const minThreshold = parseInt(formData.min_threshold);

    if (isNaN(price) || price < 0) {
      toast({
        title: "Erreur",
        description: "Le prix doit être un nombre valide",
        variant: "destructive",
      });
      return;
    }

    if (isNaN(quantity) || quantity < 0) {
      toast({
        title: "Erreur",
        description: "La quantité doit être un nombre valide",
        variant: "destructive",
      });
      return;
    }

    if (isNaN(minThreshold) || minThreshold < 0) {
      toast({
        title: "Erreur",
        description: "Le seuil minimum doit être un nombre valide",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      let imageUrl = formData.image_url;
      
      // Upload image if a new file is selected
      if (selectedFile) {
        setUploadingImage(true);
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `products/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(filePath, selectedFile);

        if (uploadError) {
          throw new Error(`Erreur lors de l'upload: ${uploadError.message}`);
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('product-images')
          .getPublicUrl(filePath);
          
        imageUrl = publicUrl;
        setUploadingImage(false);
      }

      const productData = {
        name: formData.name.trim(),
        price,
        quantity,
        min_threshold: minThreshold,
        barcode: formData.barcode.trim() || undefined,
        image_url: imageUrl || undefined,
      };

      let success = false;

      if (isEdit && editingProduct) {
        // Delete old image if replacing with new one
        if (selectedFile && editingProduct.image_url) {
          const oldImagePath = editingProduct.image_url.split('/').slice(-2).join('/');
          await supabase.storage.from('product-images').remove([oldImagePath]);
        }
        success = await updateProduct(editingProduct.id, productData);
      } else {
        success = await addProduct(productData);
      }

      if (success) {
        onClose();
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const handleBarcodeChange = (value: string) => {
    setFormData(prev => ({ ...prev, barcode: value }));
    
    // Auto-search when barcode is complete (typically 8, 12, or 13 digits)
    if (value.length >= 8 && /^\d+$/.test(value)) {
      // Debounce the search to avoid too many API calls
      if (barcodeSearchTimeout.current) {
        clearTimeout(barcodeSearchTimeout.current);
      }
      barcodeSearchTimeout.current = setTimeout(() => {
        handleBarcodeScanned(value);
      }, 1000);
    }
  };

  const handleBarcodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && formData.barcode.trim()) {
      e.preventDefault();
      if (barcodeSearchTimeout.current) {
        clearTimeout(barcodeSearchTimeout.current);
      }
      handleBarcodeScanned(formData.barcode.trim());
    }
  };

  const handleBarcodeScanned = async (barcode: string) => {
    setFormData(prev => ({ ...prev, barcode }));
    
    // Don't lookup if editing existing product or if barcode is empty
    if (isEdit || !barcode.trim()) {
      return;
    }

    // Vérifier le cache d'abord
    const cacheKey = `barcode_lookup_${barcode.trim()}`;
    const cacheDuration = 24 * 60 * 60 * 1000; // 24 heures
    
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const cachedItem = JSON.parse(cached);
        const cacheAge = Date.now() - cachedItem.timestamp;
        
        if (cacheAge < cacheDuration && cachedItem.data?.found && cachedItem.data?.unified) {
          console.log('[BARCODE] Using cached result');
          const unified = cachedItem.data.unified;
          
          // Auto-fill form fields avec données en cache
          setFormData(prev => ({
            ...prev,
            name: unified.title || prev.name,
          }));

          // Set image preview if available
          if (unified.images && unified.images.length > 0) {
            setImagePreview(unified.images[0]);
            setFormData(prev => ({ ...prev, image_url: unified.images[0] }));
          }

          const confidencePercent = Math.round(unified.confidence * 100);
          toast({
            title: "✅ Produit trouvé (cache)",
            description: `${unified.title} - Confiance: ${confidencePercent}% - ${unified.sources.length} source(s)`,
          });
          return;
        }
      }
    } catch (cacheError) {
      console.warn('[BARCODE] Cache read error:', cacheError);
    }

    setLookingUpBarcode(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('barcode-lookup', {
        body: { barcode: barcode.trim() }
      });

      if (error) {
        console.error('Barcode lookup error:', error);
        toast({
          title: "Recherche impossible",
          description: "Impossible de rechercher le produit dans les bases de données",
          variant: "destructive",
        });
        return;
      }

      console.log('[BARCODE] API Response:', data);

      if (data.found && data.unified) {
        const unified = data.unified;
        
        // Mettre en cache le résultat
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        } catch (cacheError) {
          console.warn('[BARCODE] Cache write error:', cacheError);
        }
        
        // Auto-fill form fields
        setFormData(prev => ({
          ...prev,
          name: unified.title || prev.name,
        }));

        // Set image preview if available
        if (unified.images && unified.images.length > 0) {
          setImagePreview(unified.images[0]);
          setFormData(prev => ({ ...prev, image_url: unified.images[0] }));
        }

        const confidencePercent = Math.round(unified.confidence * 100);
        const sourcesText = unified.sources.length > 3 
          ? `${unified.sources.slice(0, 3).join(', ')} et ${unified.sources.length - 3} autre(s)`
          : unified.sources.join(', ');

        toast({
          title: "✅ Produit trouvé !",
          description: `${unified.title}${unified.brand ? ' - ' + unified.brand : ''}\nConfiance: ${confidencePercent}%\nSources: ${sourcesText}`,
        });
      } else if (!data.found) {
        // Mettre en cache le résultat négatif avec TTL court
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        } catch (cacheError) {
          console.warn('[BARCODE] Cache write error:', cacheError);
        }
        
        toast({
          title: "ℹ️ Produit non trouvé",
          description: `Ce code-barres n'a pas été trouvé dans les bases de données interrogées (${data.meta?.queried_sources?.join(', ') || 'sources multiples'})`,
        });
      }
    } catch (err) {
      console.error('Barcode lookup error:', err);
      toast({
        title: "Erreur de recherche",
        description: "Une erreur est survenue lors de la recherche",
        variant: "destructive",
      });
    } finally {
      setLookingUpBarcode(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Type de fichier invalide",
          description: "Veuillez sélectionner une image",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Fichier trop volumineux",
          description: "La taille maximum est de 5MB",
          variant: "destructive",
        });
        return;
      }

      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-600" />
            {isEdit ? 'Modifier le produit' : 'Ajouter un produit'}
          </DialogTitle>
          <DialogDescription>
            {isEdit 
              ? 'Modifiez les informations du produit ci-dessous.'
              : 'Ajoutez un nouveau produit à votre inventaire.'
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom du produit *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Nom du produit"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Prix (€) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantité *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="min_threshold">Seuil minimum *</Label>
              <Input
                id="min_threshold"
                type="number"
                min="0"
                placeholder="10"
                value={formData.min_threshold}
                onChange={(e) => setFormData(prev => ({ ...prev, min_threshold: e.target.value }))}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="barcode">Code-barres</Label>
                <BarcodeScanner
                  onBarcodeScanned={handleBarcodeScanned}
                  autoStart
                  trigger={
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      disabled={loading || lookingUpBarcode}
                    >
                      {lookingUpBarcode ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Recherche...
                        </>
                      ) : (
                        <>
                          <Camera className="w-4 h-4 mr-2" />
                          Scanner
                        </>
                      )}
                    </Button>
                  }
                />
              </div>
              <Input
                ref={barcodeInputRef}
                id="barcode"
                type="text"
                placeholder="Code-barres du produit"
                value={formData.barcode}
                onChange={(e) => handleBarcodeChange(e.target.value)}
                onKeyDown={handleBarcodeKeyDown}
                disabled={loading}
              />
              {lookingUpBarcode && (
                <div className="text-center text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Recherche dans les bases de données (OpenFoodFacts, OpenBeautyFacts, OpenLibrary...)
                </div>
              )}
            </div>

            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label>Photo du produit</Label>
              
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Aperçu du produit"
                    className="w-full h-48 object-contain rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={handleRemoveImage}
                    disabled={loading || uploadingImage}
                    className="absolute top-2 right-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-500 mb-4">
                    Sélectionnez une image pour le produit
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('image-upload')?.click()}
                    disabled={loading || uploadingImage}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choisir une image
                  </Button>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}
              
              {uploadingImage && (
                <div className="text-center text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Upload en cours...
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading || uploadingImage}>
              {(loading || uploadingImage) ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploadingImage ? 'Upload...' : isEdit ? 'Modification...' : 'Ajout...'}
                </>
              ) : (
                <>
                  <Package className="w-4 h-4 mr-2" />
                  {isEdit ? 'Modifier' : 'Ajouter'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};